/**
 * PSP/CRE Tool v2.0 - Strategic Evacuation Constants
 *
 * Purpose: Define strategic evacuation points and transport options:
 * - Train stations and medical trains
 * - Airfields for fixed-wing evacuation
 * - Bus transport for mass casualty movement
 * - Distance configurations from MTF to evacuation points
 *
 * Strategic evacuation is for STABLE patients moving:
 * - Role 3 → Train Station → Role 4  (rail)
 * - Role 3 → Airfield → Role 4       (air — managed by stratevacScheduling.js)
 *
 * NOT for Golden Hour — these are post-stabilization movements.
 *
 * DOCTRINAL NOTE — TRAIN EVACUATION:
 * Medical train evacuation has NO formal NATO doctrine (no STANAG).
 * This capability is modelled from Ukraine 2022–2024 data only
 * (Lviv–Poland/Germany rail corridor, 15,000+ casualties evacuated).
 * In a NATO operation, medical rail evacuation is a national/theater
 * capability requiring host-nation rail network access and bilateral
 * coordination. It is NOT a standard NATO medical planning assumption.
 *
 * DISTANCE MODEL:
 * Only R3→Train Station is modelled. R2B/R2E→Train Station distances
 * are not modelled — patients at R2B/R2E who are stable enough for
 * rail evacuation move to R3 first, then board the train at corps level.
 * Train is a corps-level strategic asset, same tier as fixed-wing STRATEVAC.
 *
 * Author: Dr. Tatar Radu MD
 * Date: January 1, 2026
 * Version: 2.0.0
 *
 * CHANGELOG:
 * v2.0.0 (March 8, 2026) — ENH-004 Phase 1:
 *   - Added TRAIN_FREQUENCY_OPTIONS (6 modes, mirrors STRATEVAC frequency_mode)
 *   - Added TRAIN_SCHEDULE_DEFAULTS (first_departure_day, departure_hour,
 *     frequency_mode, min_patients_for_departure, trains_per_departure)
 *   - Added TRAIN_DEPARTURE_HOUR_OPTIONS (UI select options, 0400–2200)
 *   - Added TRAIN_MIN_PATIENTS_OPTIONS (UI select options)
 *   - Added TRAIN_WAIT_PENALTY (DOW penalty for prolonged wait, mirrors
 *     BACKFLOW_RULES.DOW_WAIT_PENALTY_PER_24H in stratevacScheduling.js)
 *   - Added TRAIN_QUEUE_THRESHOLDS (warning/critical/mascal — mirrors
 *     PHA_THRESHOLDS from stratevacScheduling.js)
 *   - Added computeTrainDepartureDays() helper (mirrors computeDepartureDays())
 *   - Removed R2B/R2E→Train Station distance entries from DEFAULT_STRATEGIC_DISTANCES
 *     (R3→Train Station is the only valid planning distance — see doctrinal note)
 *   - Removed 'Romanian Military Medical Institute' from header
 *   - ESLint clean
 * v1.0.0 (January 1, 2026) — Initial release
 *
 * References:
 * - Ukraine 2022–2024 medical train evacuation (Lviv→Poland/Germany corridor)
 * - NATO STANAG 2561 (AJMedP-5) Aeromedical Evacuation (air leg reference only)
 * - US Army ATP 4-02.2 Medical Evacuation
 */

import { TRANSPORT_ICONS, FACILITY_ICONS } from '../utils/icons';

// ============================================================================
// STRATEGIC EVACUATION POINT TYPES
// ============================================================================

export const STRATEGIC_POINT_TYPES = {
  TRAIN_STATION: {
    id: 'TRAIN_STATION',
    code: 'TRN',
    label: 'Train Station',
    icon: TRANSPORT_ICONS?.TRAIN_STATION || TRANSPORT_ICONS?.TRAIN || FACILITY_ICONS?.DEPOT,
    description: 'Railway station for medical train evacuation',
    typical_capacity_per_day: 150,
    requires_infrastructure: true,
    vulnerable_to_strike: true,
    doctrinal_note: 'Ukraine-derived capability. Not a NATO STANAG.'
  },

  AIRFIELD: {
    id: 'AIRFIELD',
    code: 'AFD',
    label: 'Airfield',
    icon: FACILITY_ICONS?.AIRFIELD || TRANSPORT_ICONS?.HELICOPTER,
    description: 'Airfield for fixed-wing strategic aeromedical evacuation',
    typical_capacity_per_day: 100,
    requires_infrastructure: true,
    vulnerable_to_strike: true
  },

  HLZ: {
    id: 'HLZ',
    code: 'HLZ',
    label: 'Helicopter Landing Zone',
    icon: TRANSPORT_ICONS?.HELICOPTER,
    description: 'Forward helicopter landing zone for tactical evacuation',
    typical_capacity_per_day: 50,
    requires_infrastructure: false,
    vulnerable_to_strike: false
  },

  BUS_DEPOT: {
    id: 'BUS_DEPOT',
    code: 'BUS',
    label: 'Bus Assembly Point',
    icon: TRANSPORT_ICONS?.BUS || TRANSPORT_ICONS?.GROUND,
    description: 'Bus collection point for mass patient movement',
    typical_capacity_per_day: 200,
    requires_infrastructure: false,
    vulnerable_to_strike: false
  },

  ROLE4_HOSPITAL: {
    id: 'ROLE4_HOSPITAL',
    code: 'R4H',
    label: 'Role 4 Hospital',
    icon: FACILITY_ICONS?.ROLE4 || FACILITY_ICONS?.HOSPITAL,
    description: 'Definitive care facility — national/strategic hospital',
    typical_capacity_per_day: 500,
    requires_infrastructure: true,
    vulnerable_to_strike: false
  }
};

// ============================================================================
// MEDICAL TRAIN SPECIFICATIONS
// ============================================================================

/**
 * Medical train types — Ukraine 2022–2024 validated capacities.
 * No NATO STANAG equivalent exists.
 */
export const MEDICAL_TRAIN_SPECS = {
  STANDARD: {
    id: 'STANDARD',
    name: 'Standard Medical Train',
    nato_designation: 'MED-RAIL-STD',
    icon: TRANSPORT_ICONS?.TRAIN || TRANSPORT_ICONS?.GROUND,
    doctrinal_source: 'Ukraine 2022–2024 (Lviv corridor)',

    composition: {
      hospital_cars: 4,
      patients_per_car: 36,
      total_capacity: 144,
      icu_car: 1,
      icu_beds: 12,
      operating_car: 0,
      pharmacy_car: 1,
      staff_car: 1,
      generator_car: 1
    },

    staff: {
      physicians: 4,
      nurses: 16,
      medics: 8,
      support: 8,
      total: 36
    },

    capabilities: {
      surgical_capable: false,
      icu_capable: true,
      ventilator_positions: 12,
      blood_storage_units: 50,
      xray_capable: false,
      ultrasound_capable: true
    },

    patient_categories: {
      t2_delayed: 100,
      t3_minimal: 32,
      t4_expectant: 12
    },

    operations: {
      speed_kph: 80,
      max_speed_kph: 120,
      range_km: 1500,
      refuel_needed: false,
      loading_time_hours: 2,
      unloading_time_hours: 1.5
    },

    logistics: {
      crew_rest_required_hours: 12,
      medical_resupply_interval_km: 500,
      water_capacity_liters: 2000,
      food_meals: 500,
      generator_fuel_hours: 48
    }
  },

  ENHANCED: {
    id: 'ENHANCED',
    name: 'Enhanced Medical Train',
    nato_designation: 'MED-RAIL-ENH',
    icon: TRANSPORT_ICONS?.TRAIN || TRANSPORT_ICONS?.GROUND,
    doctrinal_source: 'Ukraine 2022–2024 (Lviv corridor)',

    composition: {
      hospital_cars: 6,
      patients_per_car: 36,
      total_capacity: 216,
      icu_car: 2,
      icu_beds: 24,
      operating_car: 1,
      pharmacy_car: 1,
      staff_car: 2,
      generator_car: 2
    },

    staff: {
      surgeons: 2,
      physicians: 6,
      anesthesiologists: 1,
      nurses: 24,
      medics: 12,
      support: 12,
      total: 57
    },

    capabilities: {
      surgical_capable: true,
      procedures_per_trip: 4,
      icu_capable: true,
      ventilator_positions: 24,
      blood_storage_units: 100,
      xray_capable: true,
      ultrasound_capable: true
    },

    patient_categories: {
      t1_immediate: 8,
      t2_delayed: 150,
      t3_minimal: 48,
      t4_expectant: 10
    },

    operations: {
      speed_kph: 70,
      max_speed_kph: 100,
      range_km: 1500,
      loading_time_hours: 3,
      unloading_time_hours: 2
    }
  },

  LIGHT: {
    id: 'LIGHT',
    name: 'Light Medical Train',
    nato_designation: 'MED-RAIL-LT',
    icon: TRANSPORT_ICONS?.TRAIN || TRANSPORT_ICONS?.GROUND,
    description: 'Rapid evacuation train for T3/ambulatory patients',
    doctrinal_source: 'Ukraine 2022–2024 (Lviv corridor)',

    composition: {
      passenger_cars: 4,
      patients_per_car: 50,
      total_capacity: 200,
      medical_car: 1,
      staff_car: 1
    },

    staff: {
      physicians: 2,
      nurses: 8,
      medics: 8,
      total: 18
    },

    capabilities: {
      surgical_capable: false,
      icu_capable: false,
      stretcher_positions: 20,
      ambulatory_positions: 180
    },

    patient_categories: {
      t3_minimal: 180,
      t2_delayed_ambulatory: 20
    },

    operations: {
      speed_kph: 100,
      max_speed_kph: 140,
      range_km: 2000,
      loading_time_hours: 1,
      unloading_time_hours: 0.5
    }
  }
};

// ============================================================================
// TRAIN STATION STANDARDS
// ============================================================================

export const TRAIN_STATION_STANDARDS = {
  LARGE: {
    id: 'LARGE',
    label: 'Large Station',
    icon: TRANSPORT_ICONS?.TRAIN_STATION || TRANSPORT_ICONS?.TRAIN || FACILITY_ICONS?.DEPOT,

    infrastructure: {
      platforms: 4,
      medical_platforms: 2,
      loading_ramps: 4,
      ambulance_parking: 30,
      bus_parking: 10,
      helicopter_pad: true
    },

    capacity: {
      trains_per_day: 6,
      patients_per_day: 800,
      peak_patients_per_day: 1200,
      holding_area_beds: 100,
      staff_on_site: 50
    },

    equipment: {
      stretchers: 100,
      wheelchairs: 50,
      loading_lifts: 4,
      ambulances_assigned: 10,
      buses_assigned: 5
    },

    distance_from_feba_km: { min: 150, typical: 300, max: 500 }
  },

  MEDIUM: {
    id: 'MEDIUM',
    label: 'Medium Station',
    icon: TRANSPORT_ICONS?.TRAIN_STATION || TRANSPORT_ICONS?.TRAIN || FACILITY_ICONS?.DEPOT,

    infrastructure: {
      platforms: 2,
      medical_platforms: 1,
      loading_ramps: 2,
      ambulance_parking: 15,
      bus_parking: 5,
      helicopter_pad: false
    },

    capacity: {
      trains_per_day: 3,
      patients_per_day: 400,
      peak_patients_per_day: 600,
      holding_area_beds: 50,
      staff_on_site: 25
    },

    equipment: {
      stretchers: 50,
      wheelchairs: 25,
      loading_lifts: 2,
      ambulances_assigned: 6,
      buses_assigned: 3
    },

    distance_from_feba_km: { min: 100, typical: 150, max: 300 }
  },

  SMALL: {
    id: 'SMALL',
    label: 'Small Station',
    icon: TRANSPORT_ICONS?.TRAIN_STATION || TRANSPORT_ICONS?.TRAIN || FACILITY_ICONS?.DEPOT,

    infrastructure: {
      platforms: 1,
      medical_platforms: 1,
      loading_ramps: 1,
      ambulance_parking: 8,
      bus_parking: 2,
      helicopter_pad: false
    },

    capacity: {
      trains_per_day: 2,
      patients_per_day: 200,
      peak_patients_per_day: 300,
      holding_area_beds: 20,
      staff_on_site: 12
    },

    equipment: {
      stretchers: 20,
      wheelchairs: 10,
      loading_lifts: 1,
      ambulances_assigned: 4,
      buses_assigned: 2
    },

    distance_from_feba_km: { min: 50, typical: 100, max: 150 }
  }
};

// ============================================================================
// AIRFIELD TYPES (for reference — scheduling managed by stratevacScheduling.js)
// ============================================================================

export const AIRFIELD_TYPES = {
  INTERNATIONAL: {
    id: 'INTERNATIONAL',
    label: 'International Airport',
    icon: FACILITY_ICONS?.AIRFIELD || TRANSPORT_ICONS?.HELICOPTER,

    runway: {
      length_m: 3500,
      width_m: 45,
      surface: 'CONCRETE',
      ils_capable: true,
      night_capable: true
    },

    capacity: {
      aircraft_per_day: 20,
      c17_capable: true,
      c130_capable: true,
      a400m_capable: true,
      civilian_medevac: true
    },

    medical_facilities: {
      patient_staging_unit: true,
      psu_beds: 100,
      medical_staff: 40,
      ambulances: 15,
      patient_decontamination: true
    },

    distance_from_feba_km: { min: 200, typical: 400, max: 800 }
  },

  MILITARY: {
    id: 'MILITARY',
    label: 'Military Airfield',
    icon: FACILITY_ICONS?.AIRFIELD || TRANSPORT_ICONS?.HELICOPTER,

    runway: {
      length_m: 2500,
      width_m: 40,
      surface: 'CONCRETE',
      ils_capable: true,
      night_capable: true
    },

    capacity: {
      aircraft_per_day: 12,
      c17_capable: true,
      c130_capable: true,
      a400m_capable: true,
      civilian_medevac: false
    },

    medical_facilities: {
      patient_staging_unit: true,
      psu_beds: 60,
      medical_staff: 25,
      ambulances: 10,
      patient_decontamination: true
    },

    distance_from_feba_km: { min: 100, typical: 200, max: 400 }
  },

  FORWARD: {
    id: 'FORWARD',
    label: 'Forward Airfield',
    icon: FACILITY_ICONS?.AIRFIELD || TRANSPORT_ICONS?.HELICOPTER,

    runway: {
      length_m: 1500,
      width_m: 30,
      surface: 'ASPHALT',
      ils_capable: false,
      night_capable: false
    },

    capacity: {
      aircraft_per_day: 6,
      c17_capable: false,
      c130_capable: true,
      a400m_capable: false,
      civilian_medevac: false
    },

    medical_facilities: {
      patient_staging_unit: false,
      holding_area: true,
      holding_beds: 20,
      medical_staff: 8,
      ambulances: 4
    },

    distance_from_feba_km: { min: 40, typical: 80, max: 150 }
  }
};

// ============================================================================
// BUS TRANSPORT STANDARDS
// ============================================================================

export const BUS_TRANSPORT_STANDARDS = {
  MEDICAL_BUS: {
    id: 'MEDICAL_BUS',
    label: 'Medical Transport Bus',
    icon: TRANSPORT_ICONS?.BUS || TRANSPORT_ICONS?.GROUND,

    capacity: {
      stretcher_positions: 12,
      seated_positions: 8,
      total_patients: 20,
      medical_staff: 2
    },

    equipment: {
      oxygen_ports: 12,
      suction: 2,
      monitor: 2,
      stretcher_mounts: 12,
      climate_control: true
    },

    operations: {
      speed_kph: 60,
      range_km: 500,
      patient_categories: ['T2', 'T3'],
      loading_time_min: 30,
      unloading_time_min: 20
    }
  },

  CONVERTED_BUS: {
    id: 'CONVERTED_BUS',
    label: 'Converted Civilian Bus',
    icon: TRANSPORT_ICONS?.BUS || TRANSPORT_ICONS?.GROUND,

    capacity: {
      stretcher_positions: 6,
      seated_positions: 30,
      total_patients: 36,
      medical_staff: 2
    },

    equipment: {
      oxygen_ports: 6,
      suction: 1,
      stretcher_mounts: 6,
      climate_control: true
    },

    operations: {
      speed_kph: 70,
      range_km: 600,
      patient_categories: ['T3'],
      loading_time_min: 20,
      unloading_time_min: 15
    }
  }
};

// ============================================================================
// DEFAULT STRATEGIC DISTANCES
// NOTE: Only R3→Train Station is modelled for rail evacuation.
// R2B/R2E→Train Station are not included — patients at those echelons
// move to R3 first. Train is a corps-level asset.
// ============================================================================

export const DEFAULT_STRATEGIC_DISTANCES = {
  // Role 3 (Corps) → Train Station
  ROLE3: {
    to_train_station_km: {
      min: 20,
      default: 40,
      max: 80,
      typical_time_ground_min: 60,
      label: 'Role 3 → Train Station'
    },
    to_airfield_km: {
      min: 10,
      default: 30,
      max: 60,
      typical_time_ground_min: 45,
      label: 'Role 3 → Airfield'
    },
    to_role4_direct_km: {
      min: 50,
      default: 100,
      max: 300,
      typical_time_air_min: 60,
      label: 'Role 3 → Role 4 (Air)'
    }
  },

  // Train Station → Role 4 (by medical train)
  TRAIN_TO_ROLE4: {
    distance_km: {
      min: 200,
      default: 500,
      max: 1500,
      typical_time_train_hours: 6,
      label: 'Train Station → Role 4'
    }
  },

  // Airfield → Role 4 (by fixed-wing — reference for timeline display)
  AIRFIELD_TO_ROLE4: {
    distance_km: {
      min: 300,
      default: 800,
      max: 3000,
      typical_time_air_hours: 2,
      label: 'Airfield → Role 4'
    }
  }
};

// ============================================================================
// TRAIN SCHEDULE — FREQUENCY OPTIONS
// Mirrors STRATEVAC frequency_mode from stratevacScheduling.js
// computeTrainDepartureDays() uses these to generate departure day arrays
// ============================================================================

export const TRAIN_FREQUENCY_OPTIONS = {
  DAILY: {
    id: 'DAILY',
    label: 'Daily (every day)',
    interval_days: 1,
    departures_per_10_days: 10,
    description: 'One departure per day throughout the operation'
  },
  ALTERNATE_DAYS: {
    id: 'ALTERNATE_DAYS',
    label: 'Every 2 days',
    interval_days: 2,
    departures_per_10_days: 5,
    description: 'Departure every other day — typical Ukraine corridor frequency'
  },
  EVERY_3_DAYS: {
    id: 'EVERY_3_DAYS',
    label: 'Every 3 days',
    interval_days: 3,
    departures_per_10_days: 3,
    description: 'One departure every 3 days — limited rail access'
  },
  TWICE_WEEKLY: {
    id: 'TWICE_WEEKLY',
    label: 'Twice weekly',
    interval_days: 3,
    departures_per_10_days: 3,
    description: 'Approx. twice per week — coordinated with civilian rail schedule'
  },
  WEEKLY: {
    id: 'WEEKLY',
    label: 'Weekly',
    interval_days: 7,
    departures_per_10_days: 1,
    description: 'One departure per week — severely constrained rail access'
  },
  ON_DEMAND: {
    id: 'ON_DEMAND',
    label: 'On demand (min patients)',
    interval_days: null,
    departures_per_10_days: null,
    description: 'Train departs when minimum patient threshold is reached'
  }
};

// ============================================================================
// TRAIN SCHEDULE — DEFAULT VALUES
// These seed the Tab 3 UI train schedule state on first load.
// Stored in scenario as: strategic_evac_config.train_schedule
// ============================================================================

export const TRAIN_SCHEDULE_DEFAULTS = {
  frequency_mode: 'ALTERNATE_DAYS',   // Most common Ukraine-derived pattern
  first_departure_day: 2,             // Day 2 — allows patient accumulation on Day 1
  departure_hour: 6,                  // 0600 local — morning departure
  trains_per_departure: 1,            // 1 train per scheduled departure
  min_patients_for_departure: 50,     // ON_DEMAND mode only: min patients to trigger
  station_type: 'MEDIUM',            // Default station size
  train_type: 'STANDARD'             // Default train type
};

// ============================================================================
// TRAIN DEPARTURE HOUR OPTIONS
// UI select options for departure_hour field.
// Mirrors STRATEVAC departure_hour pattern from stratevacScheduling.js.
// ============================================================================

export const TRAIN_DEPARTURE_HOUR_OPTIONS = [
  { value: 4,  label: '0400 (Early morning)' },
  { value: 6,  label: '0600 (Dawn departure)' },
  { value: 8,  label: '0800 (Morning)' },
  { value: 10, label: '1000 (Mid-morning)' },
  { value: 12, label: '1200 (Noon)' },
  { value: 14, label: '1400 (Afternoon)' },
  { value: 16, label: '1600 (Late afternoon)' },
  { value: 18, label: '1800 (Evening)' },
  { value: 20, label: '2000 (Night departure)' },
  { value: 22, label: '2200 (Late night)' }
];

// ============================================================================
// TRAIN MINIMUM PATIENTS OPTIONS
// UI select options for ON_DEMAND mode min_patients_for_departure.
// ============================================================================

export const TRAIN_MIN_PATIENTS_OPTIONS = [
  { value: 20,  label: '20 patients (light load — any departure)' },
  { value: 30,  label: '30 patients' },
  { value: 50,  label: '50 patients (standard threshold)' },
  { value: 75,  label: '75 patients' },
  { value: 100, label: '100 patients (full load required)' },
  { value: 120, label: '120 patients (near-capacity — STANDARD train)' }
];

// ============================================================================
// TRAINS PER DEPARTURE OPTIONS
// UI select for trains_per_departure (multiple trains in convoy)
// ============================================================================

export const TRAINS_PER_DEPARTURE_OPTIONS = [
  { value: 1, label: '1 train per departure' },
  { value: 2, label: '2 trains per departure (convoy)' },
  { value: 3, label: '3 trains per departure (mass evac)' }
];

// ============================================================================
// TRAIN QUEUE THRESHOLDS
// Station holding area thresholds — mirrors PHA_THRESHOLDS from
// stratevacScheduling.js. Used by Tab 5 reports and calculator.
// Based on station holding_area_beds capacity.
// ============================================================================

export const TRAIN_QUEUE_THRESHOLDS = {
  WARNING_PERCENT: 70,    // Station holding area at 70% → warn
  CRITICAL_PERCENT: 90,   // Station holding area at 90% → critical
  MASCAL_PERCENT: 100,    // Station overflow → MASCAL / backflow to R3
  MAX_WAIT_WARNING_HOURS: 48,   // >48h wait → warning
  MAX_WAIT_CRITICAL_HOURS: 72,  // >72h wait → critical; DOW penalty applies
  doctrinal_basis: 'Ukraine 2022–2024 operational data'
};

// ============================================================================
// TRAIN WAIT PENALTY
// DOW penalty for patients waiting beyond MAX_WAIT_CRITICAL_HOURS at
// the train station holding area.
// Mirrors BACKFLOW_RULES.DOW_WAIT_PENALTY_PER_24H from stratevacScheduling.js.
// Applied in calculator after wait exceeds TRAIN_QUEUE_THRESHOLDS.MAX_WAIT_CRITICAL_HOURS.
// ============================================================================

export const TRAIN_WAIT_PENALTY = {
  DOW_PENALTY_PER_24H_BEYOND_THRESHOLD: 0.015,  // 1.5% additional DOW per 24h beyond 72h
  MAX_PENALTY_MULTIPLIER: 0.06,                  // Cap at 6% additional DOW
  THRESHOLD_HOURS: 72,                           // Same threshold as STRATEVAC air
  doctrinal_basis: 'Mirrors stratevacScheduling.js BACKFLOW_RULES — Ukraine data'
};

// ============================================================================
// STRATEGIC EVACUATION ROUTE SCHEMA
// ============================================================================

export const STRATEGIC_ROUTE_SCHEMA = {
  route_id: '',
  name: '',

  origin: {
    mtf_id: '',
    mtf_type: '',       // ROLE3 only for train evacuation
    mtf_name: ''
  },

  intermediate: {
    point_id: '',
    point_type: '',     // TRAIN_STATION or AIRFIELD
    point_name: '',
    distance_from_origin_km: 0,
    transport_method: '',
    travel_time_min: 0
  },

  destination: {
    facility_id: '',
    facility_name: '',
    distance_from_intermediate_km: 0,
    transport_method: '',   // MEDICAL_TRAIN or FIXED_WING
    travel_time_hours: 0
  },

  total: {
    distance_km: 0,
    travel_time_hours: 0,
    patient_categories: [],
    capacity_per_day: 0,
    requires_escort: false
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Compute departure days for a train schedule across an operation.
 * Mirrors computeDepartureDays() from stratevacScheduling.js.
 *
 * @param {object} schedule - Train schedule config
 * @param {string} schedule.frequency_mode - Key from TRAIN_FREQUENCY_OPTIONS
 * @param {number} schedule.first_departure_day - First departure day (1-based)
 * @param {number} operationDuration - Total operation days
 * @returns {number[]} Array of departure days (1-based)
 */
export const computeTrainDepartureDays = (schedule, operationDuration) => {
  if (!schedule || !operationDuration) return [];

  const { frequency_mode, first_departure_day } = schedule;
  const firstDay = first_departure_day ?? TRAIN_SCHEDULE_DEFAULTS.first_departure_day;

  if (frequency_mode === 'ON_DEMAND') {
    // ON_DEMAND: calculator handles departure triggers dynamically
    // Return empty array — calculator checks min_patients_for_departure daily
    return [];
  }

  const option = TRAIN_FREQUENCY_OPTIONS[frequency_mode];
  if (!option || !option.interval_days) return [];

  const days = [];
  for (let day = firstDay; day <= operationDuration; day += option.interval_days) {
    days.push(day);
  }
  return days;
};

/**
 * Get effective daily capacity for a train schedule.
 * @param {object} schedule - Train schedule config
 * @returns {number} Patients per departure
 */
export const getTrainCapacityPerDeparture = (schedule) => {
  if (!schedule) return 0;
  const trainSpec = MEDICAL_TRAIN_SPECS[schedule.train_type ?? TRAIN_SCHEDULE_DEFAULTS.train_type];
  const trainsPerDep = schedule.trains_per_departure ?? TRAIN_SCHEDULE_DEFAULTS.trains_per_departure;
  return (trainSpec?.composition?.total_capacity ?? 0) * trainsPerDep;
};

/**
 * Create a strategic evacuation point configuration
 * @param {string} type - Point type from STRATEGIC_POINT_TYPES
 * @param {object} config - Configuration overrides
 * @returns {object} Complete evacuation point configuration
 */
export const createStrategicEvacuationPoint = (type, config = {}) => {
  const pointType = STRATEGIC_POINT_TYPES[type];
  if (!pointType) {
    return null;
  }

  return {
    id: config.id || `${type.toLowerCase()}_${Date.now()}`,
    type,
    ...pointType,
    name: config.name || pointType.label,
    location: {
      position_feba_km: config.position_feba_km || 100,
      coordinates: config.coordinates || null,
      region: config.region || 'Unknown'
    },
    distances: {
      from_role3_km: config.from_role3_km || DEFAULT_STRATEGIC_DISTANCES.ROLE3.to_train_station_km.default,
      to_role4_km: config.to_role4_km || DEFAULT_STRATEGIC_DISTANCES.TRAIN_TO_ROLE4.distance_km.default
    },
    capacity: config.capacity || pointType.typical_capacity_per_day,
    status: config.status || 'OPERATIONAL',
    notes: config.notes || ''
  };
};

/**
 * Calculate strategic evacuation time for R3→Train Station→R4 leg.
 * @param {object} distances - { r3_to_station_km, station_to_r4_km }
 * @param {string} trainType - Key from MEDICAL_TRAIN_SPECS
 * @returns {object} Time calculations
 */
export const calculateStrategicEvacTime = (distances = {}, trainType = 'STANDARD') => {
  const groundSpeedKph = 40;
  const trainSpec = MEDICAL_TRAIN_SPECS[trainType];
  const trainSpeedKph = trainSpec?.operations?.speed_kph ?? 80;
  const loadingTimeMin = (trainSpec?.operations?.loading_time_hours ?? 2) * 60;

  const r3ToStation = distances.r3_to_station_km
    ?? DEFAULT_STRATEGIC_DISTANCES.ROLE3.to_train_station_km.default;
  const stationToR4 = distances.station_to_r4_km
    ?? DEFAULT_STRATEGIC_DISTANCES.TRAIN_TO_ROLE4.distance_km.default;

  const groundTimeMin = (r3ToStation / groundSpeedKph) * 60;
  const transitTimeMin = (stationToR4 / trainSpeedKph) * 60;
  const totalTimeMin = groundTimeMin + loadingTimeMin + transitTimeMin;

  return {
    origin: 'ROLE3',
    intermediate: 'TRAIN_STATION',
    distances: {
      r3_to_station_km: r3ToStation,
      station_to_r4_km: stationToR4,
      total_km: r3ToStation + stationToR4
    },
    times: {
      ground_to_station_min: Math.round(groundTimeMin),
      loading_staging_min: loadingTimeMin,
      transit_to_r4_min: Math.round(transitTimeMin),
      total_min: Math.round(totalTimeMin),
      total_hours: (totalTimeMin / 60).toFixed(1)
    },
    transport_method: {
      leg1: 'GROUND_AMBULANCE',
      leg2: 'MEDICAL_TRAIN'
    }
  };
};

/**
 * Get train type options for UI select
 * @returns {Array} Train type options
 */
export const getTrainTypeOptions = () => {
  return Object.keys(MEDICAL_TRAIN_SPECS).map(key => {
    const train = MEDICAL_TRAIN_SPECS[key];
    return {
      value: key,
      label: train.name,
      capacity: train.composition.total_capacity,
      icon: train.icon,
      speed_kph: train.operations.speed_kph,
      surgical: train.capabilities.surgical_capable
    };
  });
};

/**
 * Calculate daily strategic evacuation capacity (combined rail + air)
 * @param {object} config - Evacuation configuration
 * @returns {object} Capacity breakdown
 */
export const calculateStrategicCapacity = (config = {}) => {
  const trainCapacity = config.trains_per_day
    ? config.trains_per_day * (MEDICAL_TRAIN_SPECS[config.train_type]?.composition.total_capacity || 144)
    : 0;

  const airCapacity = config.flights_per_day
    ? config.flights_per_day * (config.patients_per_flight || 50)
    : 0;

  const busCapacity = config.buses
    ? config.buses * (config.bus_trips_per_day || 4) * 20
    : 0;

  const total = trainCapacity + airCapacity + busCapacity;

  return {
    train_capacity_per_day: trainCapacity,
    air_capacity_per_day: airCapacity,
    bus_capacity_per_day: busCapacity,
    total_capacity_per_day: total,
    breakdown: {
      train_percent: total > 0 ? Math.round((trainCapacity / total) * 100) : 0,
      air_percent: total > 0 ? Math.round((airCapacity / total) * 100) : 0,
      bus_percent: total > 0 ? Math.round((busCapacity / total) * 100) : 0
    }
  };
};

// ============================================================================
// UKRAINE 2022–2024 LESSONS LEARNED
// ============================================================================

export const UKRAINE_STRATEGIC_EVAC_LESSONS = {
  medical_trains: {
    usage: 'Poland–Ukraine medical trains evacuated 15,000+ casualties',
    capacity: 'Standard trains handled 100–150 patients per trip',
    frequency: 'Peak: 2–3 trains per day from Lviv westward',
    lessons: [
      'Rail infrastructure critical — track damage disrupted evacuation',
      'Need multiple station options due to strike risk',
      'Loading time is bottleneck — need efficient patient staging',
      'ICU cars essential for ventilated patients'
    ]
  },

  strategic_airlift: {
    usage: 'NATO allies provided C-17, A400M, civilian medevac flights',
    capacity: '50–100 patients per large aircraft',
    challenges: [
      'Airfield security — forward airfields targeted',
      'Air superiority required for fixed-wing operations',
      'Night operations preferred for security'
    ]
  },

  bus_transport: {
    usage: 'Massive use of converted buses for T3 evacuation',
    capacity: 'Civilian buses handled 30–40 ambulatory patients',
    advantages: [
      'Flexible — not dependent on rail/airfield infrastructure',
      'Numerous — easy to requisition from civilian sector',
      'Fast loading/unloading'
    ]
  },

  infrastructure_vulnerability: {
    rail: 'Multiple track cuts required repair, but rail was resilient overall',
    airfields: 'Forward airfields frequently targeted — limited utility',
    roads: 'Road network remained functional throughout',
    recommendation: 'Multi-modal capability essential — do not depend on single method'
  }
};

// ============================================================================
// EXPORT SUMMARY
// ============================================================================

const trainEvacuationExports = {
  STRATEGIC_POINT_TYPES,
  MEDICAL_TRAIN_SPECS,
  TRAIN_STATION_STANDARDS,
  AIRFIELD_TYPES,
  BUS_TRANSPORT_STANDARDS,
  DEFAULT_STRATEGIC_DISTANCES,
  STRATEGIC_ROUTE_SCHEMA,
  UKRAINE_STRATEGIC_EVAC_LESSONS,

  // ENH-004: Train scheduling constants (v2.0.0)
  TRAIN_FREQUENCY_OPTIONS,
  TRAIN_SCHEDULE_DEFAULTS,
  TRAIN_DEPARTURE_HOUR_OPTIONS,
  TRAIN_MIN_PATIENTS_OPTIONS,
  TRAINS_PER_DEPARTURE_OPTIONS,
  TRAIN_QUEUE_THRESHOLDS,
  TRAIN_WAIT_PENALTY,

  // Helper functions
  computeTrainDepartureDays,
  getTrainCapacityPerDeparture,
  createStrategicEvacuationPoint,
  calculateStrategicEvacTime,
  getTrainTypeOptions,
  calculateStrategicCapacity
};

export default trainEvacuationExports;
